// Play one word out of the learner's own recording.
//
// The point is the comparison: the model word, then the same word in their
// voice, back to back. Playing the whole phrase for that made them hunt for
// the word inside their own sentence, which is exactly the work the app is
// meant to save them.
//
// Why Web Audio and not an <audio> element with currentTime: MediaRecorder
// writes webm without a duration header, so seeking in it is unreliable and
// on some browsers silently plays from the start. Decoding the whole clip once
// and playing a slice of the buffer always lands where it should.

let ctx = null;
let current = null;               // the source that is playing, so it can be cut off
// Keyed by the Blob itself, not by an object URL. A URL can be revoked while
// the sound it points at is still on screen, and then playback fails with
// nothing to show for it; the Blob stays valid as long as anything holds it.
// Weak, so a clip is forgotten when the screen that owns it goes.
const decodedBlobs = new WeakMap();
const decoded = new Map();        // object URL -> AudioBuffer, for url callers
// Only the last few clips are ever wanted again, and a decoded minute of audio
// is megabytes, so the map is capped rather than left to grow all session.
const KEEP_DECODED = 3;

const context = () => {
    if (!ctx) {
        const Ctor = window.AudioContext || window.webkitAudioContext;
        if (!Ctor) return null;
        ctx = new Ctor();
    }
    return ctx;
};

// Hand the audio device back when nothing is playing.
//
// Housekeeping, not a fix for anything: a context left running holds the audio
// device for no reason. It was written while chasing a "synthesis-failed" in the
// browser voice, and a test settled that a live AudioContext does NOT break
// speech synthesis, so do not read this as the cure for that.
//
// Suspending rather than closing keeps the decoded clips, so replaying a word
// costs nothing beyond a resume.
const releaseDevice = () => {
    if (!ctx || current || ctx.state !== 'running') return;
    ctx.suspend().catch(() => { /* already gone */ });
};

/** Stop whatever slice is playing. Safe to call when nothing is. */
export function stopClip() {
    if (!current) return;
    try { current.onended = null; current.stop(); } catch { /* already finished */ }
    current = null;
    releaseDevice();
}

/**
 * Play `source` from `start` to `end` (seconds). Omit both to play the lot.
 *
 * `source` is the recorded Blob, or an object URL for it. Prefer the Blob:
 * a URL has to be fetched back and can have been revoked in the meantime.
 *
 * Resolves when the audio finishes, or rejects if the clip cannot be decoded.
 */
export async function playClipSlice(source, start, end, onInfo = null) {
    if (!source) return;
    const ac = context();
    if (!ac) throw new Error('No Web Audio');
    stopClip();
    // Safari and Chrome both suspend the context until a gesture; every caller
    // is inside a tap, so this resumes rather than failing silently.
    if (ac.state === 'suspended') { try { await ac.resume(); } catch { /* noop */ } }

    const isBlob = typeof source !== 'string';
    let buffer = isBlob ? decodedBlobs.get(source) : decoded.get(source);
    if (!buffer) {
        const bytes = isBlob ? await source.arrayBuffer()
            : await (await fetch(source)).arrayBuffer();
        buffer = await ac.decodeAudioData(bytes);
        if (isBlob) {
            decodedBlobs.set(source, buffer);
        } else {
            decoded.set(source, buffer);
            while (decoded.size > KEEP_DECODED) decoded.delete(decoded.keys().next().value);
        }
    }

    // Speechace's timings come from its own copy of the audio, so they can run
    // a hair past the end of the buffer. Clamped rather than trusted, and a
    // slice too short to hear falls back to the whole clip.
    const from = Math.max(0, Math.min(Number(start) || 0, buffer.duration));
    const to = end == null ? buffer.duration
        : Math.max(from, Math.min(Number(end) || 0, buffer.duration));
    const useWhole = to - from < 0.06;
    // Diagnostic: what the slice was actually cut from, for the Android
    // whole-phrase playback bug.
    if (onInfo) {
        try {
            onInfo({ duration: buffer.duration, from, to, use_whole: useWhole,
                     sample_rate: buffer.sampleRate });
        } catch { /* never break playback over a diagnostic */ }
    }

    return new Promise((resolve) => {
        const src = ac.createBufferSource();
        src.buffer = buffer;
        src.connect(ac.destination);
        src.onended = () => {
            if (current === src) current = null;
            releaseDevice();
            resolve();
        };
        current = src;
        if (useWhole) src.start();
        // A touch either side, because a word cut exactly on its boundaries
        // loses the start of the consonant and sounds clipped.
        else src.start(0, Math.max(0, from - 0.06), (to - from) + 0.12);
    });
}

